import { getApiBaseUrl } from '../config/constants';
import { FirstStepSignupConfig } from '../types/workshopFirstStepSignup';

const API_BASE_URL = getApiBaseUrl();

export interface Meeting {
  meeting_code: string;
  name: string;
  program_id: string;
  kind: string;
  description?: string;
  format?: string;
  location?: string;
  schedule_day?: string;
  schedule_start_time?: string;
  schedule_duration_minutes?: number;
  timezone?: string;
  link_visibility?: string;
  /** Who can join: anyone | meeting_key | approval_required | invite_only. Default (legacy) anyone. */
  join_rule?: string;
  workshop_first_session_date?: string;
  workshop_duration_minutes?: number;
  created_at?: string;
  updated_at?: string;
  /** Short tagline for list; if absent, display format in first column. API may return tag_line (snake_case). */
  tagline?: string;
  tag_line?: string;
  /** open = Open to Visitors, closed = Those Seeking Help */
  access_type?: 'open' | 'closed';
  /** Men | Mixed | Transgender | Women | women_lgbtq */
  gender?: string;
  language?: string;
  /** When format is telemeeting */
  telemeeting_dial_in?: string;
  telemeeting_passcode?: string;
  /** Pass phrase (only returned to meeting admins for Settings) */
  join_key?: string;
  phone_list_visibility_mode?: 'off' | 'verified_only' | 'managers_only';
  /** Workshop Zoom link; returned only to confirmed members and admins */
  zoom_url?: string;
  workshop_calendar?: WorkshopCalendar;
  workshop_updates?: WorkshopUpdate[];
  first_step_signup?: FirstStepSignupConfig;
}

export interface WorkshopCalendar {
  current_step?: number | null;
  step_title?: string;
  step_started_date?: string;
  where_we_are?: string;
  likely_complete?: string;
  next_step_starts?: string;
  published?: boolean;
}

export interface WorkshopUpdate {
  id: string;
  date: string;
  body: string;
}

export type NameListMode = 'hidden' | 'when_workshop_starts' | 'now';

export interface MeetingMemberPreferences {
  include_on_printed_phone_list: boolean;
  include_email_on_list: boolean;
  name_list_mode: NameListMode;
  sponsor_willingness: 'no' | 'yes' | 'temporary';
  display_preference: 'hidden' | 'name_only' | 'name_and_location' | 'name_and_phone';
  email_notifications_enabled: boolean;
}

export interface MeetingMemberStatus extends MeetingMemberPreferences {
  is_member: boolean;
  is_admin: boolean;
  is_manager: boolean;
  is_verified: boolean;
  service_roles: string[];
  verification_requested_at?: string | null;
  registration_state?: 'none' | 'interest' | 'confirmed';
  interest_at?: string | null;
  confirmed_at?: string | null;
  interest_responses?: Record<string, string> | null;
}

export interface WorkshopInterestPayload {
  interest_responses: Record<string, string>;
}

export interface MeetingMember extends MeetingMemberStatus {
  user_id: string;
  display_name: string;
  email?: string;
  joined_at?: string;
}

export interface MeetingPhoneListEntry {
  user_id: string;
  display_name: string;
  phone: string;
  email?: string;
  sponsor_willingness: string;
}

export interface CreateMeetingParams {
  name: string;
  meeting_code?: string;  // optional; server assigns code (e.g. SAA7)
  program_id?: string;
  kind?: string;
  description?: string;
  format?: string;
  location?: string;
  schedule_day?: string;
  schedule_start_time?: string;
  schedule_duration_minutes?: number;
  schedule_duration_custom?: string;
  timezone?: string;
  link_visibility?: string;
  workshop_first_session_date?: string;
  workshop_duration_minutes?: number;
  workshop_duration_custom?: string;
  tagline?: string;
  access_type?: 'open' | 'closed';
  gender?: string;
  language?: string;
  telemeeting_dial_in?: string;
  telemeeting_passcode?: string;
}

class MeetingService {
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async makePublicRequest(endpoint: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  private async makeAuthRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = this.getAuthToken();
    if (!token) throw new Error('Please log in.');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers as Record<string, string>),
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async getMeetingByCode(code: string): Promise<Meeting> {
    const normalized = code.trim().toUpperCase();
    const { meeting } = await this.makePublicRequest(`/meetings/by-code/${encodeURIComponent(normalized)}`);
    return meeting;
  }

  /** Auth-aware meeting fetch (includes zoom_url when caller is confirmed member or admin). */
  async getMeetingByCodeAuthed(code: string): Promise<Meeting> {
    const normalized = code.trim().toUpperCase();
    const { meeting } = await this.makeAuthRequest(`/meetings/by-code/${encodeURIComponent(normalized)}`);
    return meeting;
  }

  /** Get meeting by code with auth; returns join_key when caller is meeting admin (for Settings). */
  async getMeetingByCodeForAdmin(code: string): Promise<Meeting> {
    const normalized = code.trim().toUpperCase();
    const { meeting } = await this.makeAuthRequest(`/meetings/by-code/${encodeURIComponent(normalized)}`);
    return meeting;
  }

  /**
   * Resolve join-by-URL: validate meeting code + pass phrase; returns minimal meeting info or throws with friendly message.
   * Public (no auth). Use before showing "Join [name]?" confirm.
   */
  async getMeetingJoinByUrl(meetingCode: string, passPhrase: string): Promise<{ meeting_code: string; name: string }> {
    const code = meetingCode.trim().toUpperCase();
    const res = await this.makePublicRequest(
      `/meetings/join/${encodeURIComponent(code)}/${encodeURIComponent(passPhrase)}`
    );
    return res.meeting;
  }

  async checkMember(code: string): Promise<boolean> {
    const status = await this.getMemberStatus(code);
    return status.is_member;
  }

  async getMemberStatus(code: string): Promise<MeetingMemberStatus> {
    const normalized = code.trim().toUpperCase();
    const res = await this.makeAuthRequest(`/meetings/check-member/${encodeURIComponent(normalized)}`);
    return {
      is_member: !!res.is_member,
      is_admin: !!res.is_admin,
      is_manager: !!res.is_manager,
      is_verified: !!res.is_verified,
      service_roles: Array.isArray(res.service_roles) ? res.service_roles : [],
      verification_requested_at: res.verification_requested_at ?? null,
      registration_state: res.registration_state ?? 'none',
      interest_at: res.interest_at ?? null,
      confirmed_at: res.confirmed_at ?? null,
      interest_responses: res.interest_responses ?? null,
      include_on_printed_phone_list: !!res.include_on_printed_phone_list,
      include_email_on_list: !!res.include_email_on_list,
      name_list_mode: res.name_list_mode ?? 'when_workshop_starts',
      sponsor_willingness: res.sponsor_willingness ?? 'no',
      display_preference: res.display_preference ?? 'name_only',
      email_notifications_enabled: res.email_notifications_enabled !== false,
    };
  }

  async confirmWorkshopAttendance(code: string): Promise<{
    registration_state: string;
    confirmed_at: string;
    confirmation_email_sent?: boolean;
  }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/confirm-attendance`,
      { method: 'POST' }
    );
  }

  async adminConfirmWorkshopMember(
    code: string,
    memberUserId: string
  ): Promise<{
    registration_state: string;
    confirmed_at: string;
    confirmation_email_sent?: boolean;
  }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/members/${encodeURIComponent(memberUserId)}/confirm`,
      { method: 'POST' }
    );
  }

  async updateMyMemberPreferences(
    code: string,
    preferences: Partial<MeetingMemberPreferences>
  ): Promise<MeetingMemberPreferences> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/members/me/preferences`,
      { method: 'PATCH', body: JSON.stringify(preferences) }
    );
  }

  async registerWorkshopInterest(
    code: string,
    payload: WorkshopInterestPayload
  ): Promise<{
    registration_state: string;
    interest_at: string;
    interest_responses: Record<string, string>;
    confirmation_email_sent?: boolean;
  }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/register-interest`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  }

  async withdrawWorkshopInterest(code: string): Promise<{ registration_state: string }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/interest`,
      { method: 'DELETE' }
    );
  }

  /** Optional preferences when adding yourself to a meeting (join-preferences step). Pass join_key when meeting uses meeting_key join rule. */
  async joinMeeting(
    code: string,
    preferences?: {
      display_preference?: string;
      include_on_printed_phone_list?: boolean;
      include_email_on_list?: boolean;
      name_list_mode?: NameListMode;
      email_notifications_enabled?: boolean;
      sponsor_willingness?: string;
      /** Required when meeting join_rule is meeting_key */
      join_key?: string;
    }
  ): Promise<void> {
    const normalized = code.trim().toUpperCase();
    const body: Record<string, unknown> = preferences
      ? {
          display_preference: preferences.display_preference ?? 'name_only',
          include_on_printed_phone_list: preferences.include_on_printed_phone_list ?? false,
          include_email_on_list: preferences.include_email_on_list ?? false,
          name_list_mode: preferences.name_list_mode ?? 'when_workshop_starts',
          email_notifications_enabled: preferences.email_notifications_enabled ?? true,
          sponsor_willingness: preferences.sponsor_willingness ?? 'no',
        }
      : {};
    if (preferences?.join_key !== undefined && preferences.join_key !== '') {
      body.join_key = preferences.join_key;
    }
    await this.makeAuthRequest(`/meetings/join/${encodeURIComponent(normalized)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async listMembers(code: string): Promise<MeetingMember[]> {
    const normalized = code.trim().toUpperCase();
    const { members } = await this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}/members`);
    const rawMembers = Array.isArray(members) ? members : [];
    return rawMembers.map((m: any) => ({
      user_id: m.user_id,
      display_name: m.display_name,
      email: m.email || m.user_id,
      joined_at: m.joined_at,
      is_member: true,
      is_admin: !!m.is_admin,
      is_manager: !!m.is_manager,
      is_verified: !!m.is_verified,
      service_roles: Array.isArray(m.service_roles) ? m.service_roles : [],
      verification_requested_at: m.verification_requested_at ?? null,
      include_on_printed_phone_list: !!m.include_on_printed_phone_list,
      include_email_on_list: !!m.include_email_on_list,
      name_list_mode: m.name_list_mode ?? 'when_workshop_starts',
      sponsor_willingness: m.sponsor_willingness ?? 'no',
      display_preference: m.display_preference ?? 'name_only',
      email_notifications_enabled: m.email_notifications_enabled !== false,
      registration_state: m.registration_state ?? 'none',
      interest_at: m.interest_at ?? null,
      confirmed_at: m.confirmed_at ?? null,
      interest_responses: m.interest_responses ?? null,
    }));
  }

  async removeMember(code: string, userId: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async updateMemberRoles(
    code: string,
    userId: string,
    updates: {
      is_admin?: boolean;
      is_manager?: boolean;
      is_verified?: boolean;
      service_roles?: string[];
    }
  ): Promise<MeetingMember> {
    const normalized = code.trim().toUpperCase();
    const res = await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/members/${encodeURIComponent(userId)}/roles`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }
    );

    if (res?.member?.user_id) {
      const m = res.member;
      return {
        user_id: m.user_id,
        display_name: m.display_name || userId,
        joined_at: m.joined_at,
        is_member: true,
        is_admin: !!m.is_admin,
        is_manager: !!m.is_manager,
        is_verified: !!m.is_verified,
        service_roles: Array.isArray(m.service_roles) ? m.service_roles : [],
        verification_requested_at: m.verification_requested_at ?? null,
        include_on_printed_phone_list: !!m.include_on_printed_phone_list,
        include_email_on_list: !!m.include_email_on_list,
        name_list_mode: m.name_list_mode ?? 'when_workshop_starts',
        sponsor_willingness: m.sponsor_willingness ?? 'no',
        display_preference: m.display_preference ?? 'name_only',
        email_notifications_enabled: m.email_notifications_enabled !== false,
        registration_state: m.registration_state ?? 'none',
        interest_at: m.interest_at ?? null,
        confirmed_at: m.confirmed_at ?? null,
        interest_responses: m.interest_responses ?? null,
      };
    }

    // Fallback: listMembers may be forbidden for some roles; for admin UI it's fine.
    const members = await this.listMembers(normalized);
    const updated = members.find((m) => m.user_id === userId);
    if (!updated) throw new Error('Failed to update member roles');
    return updated;
  }

  async updateMeeting(
    code: string,
    updates: {
      name?: string;
      description?: string;
      join_rule?: string;
      join_key?: string;
      regenerate_pass_phrase?: boolean;
      tagline?: string;
      access_type?: string;
      gender?: string;
      language?: string;
      format?: string;
      location?: string;
      telemeeting_dial_in?: string;
      telemeeting_passcode?: string;
      phone_list_visibility_mode?: 'off' | 'verified_only' | 'managers_only';
      zoom_url?: string;
      workshop_first_session_date?: string;
      workshop_calendar?: WorkshopCalendar;
      workshop_updates?: WorkshopUpdate[];
      first_step_signup?: FirstStepSignupConfig;
    }
  ): Promise<void> {
    const normalized = code.trim().toUpperCase();
    const body: Record<string, unknown> = {};
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.description !== undefined) body.description = updates.description ?? '';
    if (updates.join_rule !== undefined) body.join_rule = updates.join_rule;
    if (updates.join_key !== undefined) body.join_key = updates.join_key;
    if (updates.regenerate_pass_phrase === true) body.regenerate_pass_phrase = true;
    if (updates.tagline !== undefined) body.tagline = updates.tagline ?? '';
    if (updates.access_type !== undefined) body.access_type = updates.access_type;
    if (updates.gender !== undefined) body.gender = updates.gender ?? '';
    if (updates.language !== undefined) body.language = updates.language ?? '';
    if (updates.format !== undefined) body.format = updates.format;
    if (updates.location !== undefined) body.location = updates.location ?? '';
    if (updates.telemeeting_dial_in !== undefined) body.telemeeting_dial_in = updates.telemeeting_dial_in ?? '';
    if (updates.telemeeting_passcode !== undefined) body.telemeeting_passcode = updates.telemeeting_passcode ?? '';
    if (updates.phone_list_visibility_mode !== undefined) body.phone_list_visibility_mode = updates.phone_list_visibility_mode;
    if (updates.zoom_url !== undefined) body.zoom_url = updates.zoom_url ?? '';
    if (updates.workshop_first_session_date !== undefined) {
      body.workshop_first_session_date = updates.workshop_first_session_date;
    }
    if (updates.workshop_calendar !== undefined) body.workshop_calendar = updates.workshop_calendar;
    if (updates.workshop_updates !== undefined) body.workshop_updates = updates.workshop_updates;
    if (updates.first_step_signup !== undefined) body.first_step_signup = updates.first_step_signup;
    await this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async requestMeetingVerification(code: string): Promise<MeetingMemberStatus> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}/verification/request`, { method: 'POST' });
    return this.getMemberStatus(normalized);
  }

  async decideMeetingVerification(
    code: string,
    memberUserId: string,
    approve: boolean
  ): Promise<MeetingMember> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/verification/${encodeURIComponent(memberUserId)}/decision`,
      { method: 'POST', body: JSON.stringify({ approve }) }
    );
    const members = await this.listMembers(normalized);
    const updated = members.find((m) => m.user_id === memberUserId);
    if (!updated) throw new Error('Failed to update member verification status');
    return updated;
  }

  async getPhoneList(code: string): Promise<{ phone_list: MeetingPhoneListEntry[] }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}/phone-list`);
  }

  async getWorkshopRoster(code: string): Promise<{
    names: { display_name: string; user_id: string }[];
    workshop_started: boolean;
  }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}/workshop/roster`);
  }

  async getWorkshopSummary(code: string): Promise<{
    confirmed_count: number;
    facilitator_name: string | null;
  }> {
    const normalized = code.trim().toUpperCase();
    return this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}/workshop/summary`);
  }

  async createMeeting(params: CreateMeetingParams): Promise<{
    meeting_code: string;
    duplicates?: string[];
    meeting_history_entry?: any;
  }> {
    const body: Record<string, unknown> = {
      name: params.name,
      program_id: params.program_id || 'saa',
      kind: params.kind || 'meeting',
      description: params.description || '',
      format: params.format || 'online',
      location: params.location ?? '',
      schedule_day: params.schedule_day || null,
      schedule_start_time: params.schedule_start_time || null,
      schedule_duration_minutes: params.schedule_duration_minutes ?? null,
      schedule_duration_custom: params.schedule_duration_custom || null,
      timezone: params.timezone || null,
      link_visibility: params.link_visibility || 'everyone',
      tagline: params.tagline ?? '',
      access_type: params.access_type ?? 'open',
      gender: params.gender ?? '',
      language: params.language ?? '',
    };
    if (params.kind === 'workshop') {
      body.workshop_first_session_date = params.workshop_first_session_date || null;
      body.workshop_duration_minutes = params.workshop_duration_minutes ?? null;
      body.workshop_duration_custom = params.workshop_duration_custom || null;
    }
    if (params.format === 'telemeeting') {
      body.telemeeting_dial_in = params.telemeeting_dial_in ?? '';
      body.telemeeting_passcode = params.telemeeting_passcode ?? '';
    }
    return this.makeAuthRequest('/meetings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async listMeetings(programId?: string): Promise<Meeting[]> {
    const qs = programId ? `?program_id=${encodeURIComponent(programId)}` : '';
    const { meetings } = await this.makePublicRequest(`/meetings${qs}`);
    return meetings || [];
  }

  async getHomeMeetings(): Promise<Meeting[]> {
    const { meetings } = await this.makeAuthRequest('/meetings/home');
    return meetings || [];
  }

  async saveToHome(code: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(`/meetings/home/${encodeURIComponent(normalized)}`, { method: 'POST' });
  }

  async removeFromHome(code: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(`/meetings/home/${encodeURIComponent(normalized)}`, { method: 'DELETE' });
  }

  async updateHomeOrder(codes: string[]): Promise<void> {
    await this.makeAuthRequest('/meetings/home/order', {
      method: 'PUT',
      body: JSON.stringify({ meeting_codes: codes }),
    });
  }

  async getFirstStepSignup(code: string): Promise<FirstStepSignupConfig> {
    const normalized = code.trim().toUpperCase();
    const res = await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/first-step-signup`
    );
    return res.first_step_signup as FirstStepSignupConfig;
  }

  async assignFirstStepSignup(
    code: string,
    weekIndex: number,
    columnIndex: number,
    userId?: string
  ): Promise<FirstStepSignupConfig> {
    const normalized = code.trim().toUpperCase();
    const body: Record<string, unknown> = { week_index: weekIndex, column_index: columnIndex };
    if (userId) body.user_id = userId;
    const res = await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/first-step-signup/assign`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return res.first_step_signup as FirstStepSignupConfig;
  }

  async removeMyFirstStepSignup(code: string): Promise<FirstStepSignupConfig> {
    const normalized = code.trim().toUpperCase();
    const res = await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/first-step-signup/assign/me`,
      { method: 'DELETE' }
    );
    return res.first_step_signup as FirstStepSignupConfig;
  }

  async lockFirstStepSignupWeek(
    code: string,
    weekIndex: number,
    locked: boolean
  ): Promise<FirstStepSignupConfig> {
    const normalized = code.trim().toUpperCase();
    const res = await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/workshop/first-step-signup/lock-week`,
      { method: 'POST', body: JSON.stringify({ week_index: weekIndex, locked }) }
    );
    return res.first_step_signup as FirstStepSignupConfig;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }
}

export const meetingService = new MeetingService();
